import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Cpu, Plane, Zap, Activity } from "lucide-react";

import KpiTile from "@/components/KpiTile";
import StatusPill from "@/components/StatusPill";
import MockBadge from "@/components/MockBadge";
import { admin, ops } from "@/lib/api";
import { fmtMs, fmtN, fmtUSD, fmtTime, shortId } from "@/lib/format";

// Live ops polling (react-query, 8s) — surfaces handle empties on error.
const POLL = { refetchInterval: 8000, retry: false };

export default function Dashboard() {
  const navigate = useNavigate();
  const { data: kpis } = useQuery({ queryKey: ["dash-kpis"], queryFn: ops.kpis, ...POLL });
  const { data: trips = [] } = useQuery({
    queryKey: ["dash-trips"],
    queryFn: () => admin.trips({ limit: 12 }),
    ...POLL,
  });
  const { data: hermesStatus } = useQuery({
    queryKey: ["dash-hermes"],
    queryFn: ops.hermesStatus,
    ...POLL,
  });
  const { data: adapters = [] } = useQuery({
    queryKey: ["dash-adapters"],
    queryFn: ops.adaptersHealth,
    ...POLL,
  });
  const { data: llmSummary } = useQuery({
    queryKey: ["dash-llm"],
    queryFn: ops.llmSummary,
    ...POLL,
  });
  const tasks = hermesStatus?.recent || [];

  return (
    <div data-testid="page-dashboard" className="p-6 md:p-8 lg:p-10 max-w-[1640px]">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div className="min-w-0">
          <div className="pj-label mb-2">· Control Room</div>
          <h1 className="font-display text-[32px] sm:text-[40px] leading-[1.02]" data-testid="dashboard-title">
            Operations
          </h1>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <MockBadge label="Mock data" />
          <button
            type="button"
            data-testid="btn-new-quote"
            onClick={() => navigate("/trips")}
            className="mono px-3 py-2 text-[11px] uppercase tracking-[0.12em] hover:opacity-90"
            style={{ background: "linear-gradient(90deg, rgb(169,130,28), rgb(158,92,44))", color: "rgb(252,249,243)" }}
          >
            + New Trip Request
          </button>
        </div>
      </div>

      {/* KPI ROW */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-px" style={{ background: "rgb(var(--ln))" }}>
        <KpiTile
          label="Bookings · Today"
          value={fmtN(kpis?.bookings_today ?? 0)}
          dataTestid="kpi-bookings-today"
          accent="rgb(32,138,78)"
        />
        <KpiTile
          label="Pipeline · 30d"
          value={fmtUSD(kpis?.pipeline?.total_usd ?? 0)}
          hint={`${kpis?.pipeline?.total_count ?? 0} bookings`}
          dataTestid="kpi-pipeline-value"
          accent="rgb(36,104,184)"
        />
        <KpiTile
          label="Conversion"
          value={`${kpis?.conversion_pct ?? 0}`}
          unit="%"
          hint="quoted → booked"
          dataTestid="kpi-conversion"
        />
        <KpiTile
          label="Avg Quote Time"
          value={fmtMs(kpis?.avg_quote_time_ms ?? 0)}
          hint="target < 60s"
          dataTestid="kpi-avg-quote-time"
          accent="rgb(176,116,18)"
        />
        <KpiTile
          label="Compliance Hold"
          value={fmtN(kpis?.pending_compliance ?? 0)}
          hint={(kpis?.alerts ?? 0) + " agent alerts"}
          dataTestid="kpi-compliance-hold"
          accent="rgb(196,52,44)"
        />
      </div>

      {/* GRID — Bookings Pipeline (2/3) + Runtime (1/3) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        {/* Funnel */}
        <section className="lg:col-span-2 pj-card p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-[22px]">Funnel · 30d</h2>
            <span className="pj-label">visitor → flown</span>
          </div>
          <div className="space-y-3">
            {[
              { label: "Visitors", v: kpis?.funnel?.visitors ?? 0, color: "rgb(140,128,110)" },
              { label: "Quote Requests", v: kpis?.funnel?.quote_requests ?? 0, color: "rgb(36,104,184)" },
              { label: "Quoted", v: kpis?.funnel?.quoted ?? 0, color: "rgb(36,104,184)" },
              { label: "Accepted Quotes", v: kpis?.funnel?.accepted_quotes ?? 0, color: "rgb(169,130,28)" },
              { label: "Bookings", v: kpis?.funnel?.booked ?? 0, color: "rgb(32,138,78)" },
            ].map((r) => {
              const max = Math.max(1, kpis?.funnel?.visitors || 1, kpis?.funnel?.quote_requests || 1);
              const pct = Math.min(100, Math.round((r.v / max) * 100));
              return (
                <div key={r.label} className="flex items-center gap-4">
                  <div
                    className="mono uppercase text-[10px] tracking-[0.12em] w-32 sm:w-44 shrink-0 truncate"
                    title={r.label}
                    style={{ color: "rgb(var(--dim))" }}
                  >
                    {r.label}
                  </div>
                  <div className="flex-1 min-w-0 h-6 relative overflow-hidden" style={{ background: "rgb(var(--surface-2))", border: "1px solid rgb(var(--ln))" }}>
                    <div
                      className="absolute inset-y-0 left-0"
                      style={{ width: `${pct}%`, background: r.color, opacity: 0.85 }}
                    />
                    <div className="absolute inset-0 flex items-center px-2 mono text-[11px] truncate">
                      {fmtN(r.v)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* HERMES Runtime */}
        <section className="pj-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-[22px]">HERMES Runtime</h2>
            <Cpu size={14} style={{ color: "rgb(var(--dim))" }} />
          </div>
          <div className="grid grid-cols-2 gap-px" style={{ background: "rgb(var(--ln))" }}>
            {tasks.length === 0 && (
              <div className="col-span-2 p-3 mono text-[11px]" style={{ color: "rgb(var(--dim))", background: "rgb(var(--surface))" }}>
                No recent tasks
              </div>
            )}
            {tasks.slice(0, 6).map((t) => (
              <div key={t.id} className="p-3 min-w-0" style={{ background: "rgb(var(--surface))" }}>
                <div className="flex items-center justify-between gap-2">
                  <span className="mono text-[10px] uppercase tracking-[0.12em] min-w-0 truncate" title={t.agent} style={{ color: "rgb(var(--dim))" }}>
                    {t.agent}
                  </span>
                  <span className="shrink-0">
                    <StatusPill state={t.state} dataTestid={`hermes-task-${t.id}`} />
                  </span>
                </div>
                <div className="mono mt-1 text-[10px]" style={{ color: "rgb(var(--muted-2))" }}>
                  {fmtMs(t.latency_ms)} · {fmtTime(t.created_at)}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2 mono text-[10px] uppercase tracking-[0.12em]" style={{ color: "rgb(var(--dim))" }}>
            <div className="min-w-0">LLM Calls<div className="text-base mono mt-1 truncate" style={{ color: "rgb(var(--fg))" }}>{llmSummary?.total_calls ?? 0}</div></div>
            <div className="min-w-0">LLM Cost<div className="text-base mono mt-1 truncate" style={{ color: "rgb(var(--fg))" }}>${(llmSummary?.total_cost_usd ?? 0).toFixed(3)}</div></div>
            <div className="min-w-0">Avg Latency<div className="text-base mono mt-1 truncate" style={{ color: "rgb(var(--fg))" }}>{fmtMs(llmSummary?.avg_latency_ms ?? 0)}</div></div>
          </div>
        </section>
      </div>

      {/* Recent Trips + Adapter Health */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <section className="lg:col-span-2 pj-card">
          <div className="flex items-center justify-between p-4" style={{ borderBottom: "1px solid rgb(var(--ln))" }}>
            <h2 className="font-display text-[18px] flex items-center gap-2">
              <Plane size={14} style={{ color: "rgb(var(--dim))" }} /> Recent Trip Requests
            </h2>
            <button
              type="button"
              data-testid="link-all-trips"
              onClick={() => navigate("/trips")}
              className="mono text-[10px] uppercase tracking-[0.14em] inline-flex items-center gap-1 hover:opacity-80"
              style={{ color: "rgb(var(--dim))" }}
            >
              View all <ArrowRight size={12} />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full mono text-[12px]">
              <thead style={{ color: "rgb(var(--dim))" }}>
                <tr className="text-left">
                  <th className="px-4 py-2 font-normal">ID</th>
                  <th className="px-4 py-2 font-normal">Route</th>
                  <th className="px-4 py-2 font-normal">Pax</th>
                  <th className="px-4 py-2 font-normal">Cabin</th>
                  <th className="px-4 py-2 font-normal">Status</th>
                  <th className="px-4 py-2 font-normal">Created</th>
                </tr>
              </thead>
              <tbody>
                {trips.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-6" style={{ color: "rgb(var(--dim))" }}>No trip requests yet.</td></tr>
                )}
                {trips.map((t) => (
                  <tr
                    key={t.id}
                    data-testid={`trip-row-${t.id}`}
                    style={{ borderTop: "1px solid rgb(var(--ln))" }}
                    className="hover:bg-[rgb(236,229,216)] cursor-pointer"
                    onClick={() => navigate(`/trips`)}
                  >
                    <td className="px-4 py-3 whitespace-nowrap" style={{ color: "rgb(var(--dim))" }}>{shortId(t.id)}</td>
                    <td className="px-4 py-3">
                      {(() => {
                        const route = (t.legs || []).map((l) => `${l.from_airport}→${l.to_airport}`).join(" · ") || "—";
                        return (
                          <span className="block max-w-[260px] truncate" title={route}>{route}</span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{t.pax}</td>
                    <td className="px-4 py-3 capitalize whitespace-nowrap">{(t.preferred_cabin || "").replace("_", " ")}</td>
                    <td className="px-4 py-3 whitespace-nowrap"><StatusPill state={t.status} /></td>
                    <td className="px-4 py-3 whitespace-nowrap" style={{ color: "rgb(var(--muted-2))" }}>{fmtTime(t.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="pj-card">
          <div className="flex items-center justify-between p-4" style={{ borderBottom: "1px solid rgb(var(--ln))" }}>
            <h2 className="font-display text-[18px] flex items-center gap-2">
              <Activity size={14} style={{ color: "rgb(var(--dim))" }} /> Adapter Health
            </h2>
            <MockBadge />
          </div>
          <div className="divide-y divide-[rgb(223,214,198)]">
            {adapters.length === 0 && (
              <div className="p-4 mono text-[11px]" style={{ color: "rgb(var(--dim))" }}>No adapters loaded.</div>
            )}
            {adapters.map((a) => (
              <div key={a.name} className="flex items-center justify-between gap-3 px-4 py-2.5" data-testid={`adapter-${a.name}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="pj-pulse shrink-0"
                    style={{
                      width: 6,
                      height: 6,
                      background: a.ok ? "rgb(32,138,78)" : "rgb(196,52,44)",
                      display: "inline-block",
                      borderRadius: 999,
                    }}
                  />
                  <span className="mono text-[12px] truncate" title={a.name} style={{ color: "rgb(var(--fg))" }}>{a.name}</span>
                </div>
                <span className="mono text-[10px] uppercase tracking-[0.14em] shrink-0 whitespace-nowrap" style={{ color: "rgb(var(--dim))" }}>
                  {a.mode} · {a.latency_ms || 0}ms
                </span>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="mt-8 mono text-[10px] uppercase tracking-[0.16em] flex items-center gap-2" style={{ color: "rgb(var(--muted-2))" }}>
        <Zap size={11} /> Live · auto-refresh 8s · mock adapters
      </div>
    </div>
  );
}
